import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Pitch } from '@/types';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  CheckCircle, 
  UserCheck, 
  Clock, 
  ArrowRight, 
  Brain,
  Edit,
  Save,
  MessageSquare,
  ThumbsUp,
  Target,
  Lightbulb,
  BookOpen,
  Sparkles,
  FileText,
  Download,
  PenTool,
  AlertCircle,
  FileSignature,
  Scale,
  TrendingUp,
  Users,
  Cpu,
  Globe,
  DollarSign,
  Lock,
  Eye,
  X
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useState, useEffect } from 'react';
import { deepseekApi } from '@/lib/deepseekApi';

interface Props {
  pitch: Pitch;
}

// AI-generated content structure for each day
interface DailyContent {
  id: string;
  day: number;
  week: number;
  title: string;
  learningObjectives: string[];
  keyConcepts: Array<{
    term: string;
    definition: string;
    examples: string[];
  }>;
  tasks: Array<{
    title: string;
    description: string;
    deliverables: string[];
    estimatedTime: number;
    resources: string[];
  }>;
  quiz: Array<{
    question: string;
    options: string[];
    correctAnswer: number;
    explanation: string;
  }>;
  resources: Array<{
    type: 'video' | 'article' | 'template' | 'tool';
    title: string;
    url: string;
  }>;
  aiGenerated: boolean;
  lastModified: string;
  generatedBy?: string;
  generatedAt?: string;
}

// Analyst notes for each day
interface AnalystNotes {
  day: number;
  notes: string;
  resources: string[];
  customizations: string[];
  founderUnderstanding: 1 | 2 | 3 | 4 | 5;
  lastUpdated: string;
}

// LOI Assessment structure
interface LOIAssessment {
  criteria: {
    id: string;
    name: string;
    description: string;
    status: 'met' | 'partial' | 'not-met' | 'pending';
    notes: string;
  }[];
  overallAssessment: string;
  recommendation: 'approve' | 'conditional' | 'reject';
  conditionalTerms?: string;
  assessedBy: string;
  assessedAt: string;
}

const WEEK_TITLES = [
  'Week 1: Architecture & Stress Testing',
  'Week 2: Unit Economics',
  'Week 3: Branding & GTM',
  'Week 4: Funding Gate',
];

// LOI assessment criteria
const LOI_CRITERIA = [
  {
    id: 'business-model',
    name: 'Business Model Validation',
    description: 'Has the founder validated the business model with real customer feedback?',
    icon: TrendingUp
  },
  {
    id: 'market-research',
    name: 'Market Research Complete',
    description: 'Has comprehensive market research been conducted and documented?',
    icon: Globe
  },
  {
    id: 'financials',
    name: 'Financial Projections',
    description: 'Are financial projections realistic, detailed, and well-documented?',
    icon: DollarSign
  },
  {
    id: 'mvp',
    name: 'MVP/Prototype Ready',
    description: 'Is there a working prototype or MVP that demonstrates core functionality?',
    icon: Cpu
  },
  {
    id: 'team',
    name: 'Team Readiness',
    description: 'Is the team prepared, committed, and capable for the next stage?',
    icon: Users
  },
  {
    id: 'traction',
    name: 'Early Traction',
    description: 'Has the founder demonstrated any early traction or customer interest?',
    icon: TrendingUp
  },
  {
    id: 'ip',
    name: 'IP & Technology',
    description: 'Is intellectual property properly protected or documented?',
    icon: FileText
  },
  {
    id: 'legal',
    name: 'Legal & Compliance',
    description: 'Are there any legal or compliance issues that need addressing?',
    icon: Scale
  }
];

export default function BootcampPanel({ pitch }: Props) {
  const { user } = useAuth();
  const { toggleBootcampDay, updatePitchStage } = useData();
  const { toast } = useToast();

  // State for verification
  const [analystVerified, setAnalystVerified] = useState(
    (pitch as any).bootcampAnalystVerified || false
  );

  // State for LOI assessment
  const [loiAssessment, setLoiAssessment] = useState<LOIAssessment>(() => {
    const saved = localStorage.getItem(`loi_assessment_${pitch.id}`);
    return saved ? JSON.parse(saved) : {
      criteria: LOI_CRITERIA.map(c => ({
        id: c.id,
        name: c.name,
        description: c.description,
        status: 'pending',
        notes: ''
      })),
      overallAssessment: '',
      recommendation: 'conditional',
      assessedBy: user?.id || '',
      assessedAt: new Date().toISOString()
    };
  });

  // State for AI-generated content - PERSISTED in localStorage
  const [dailyContent, setDailyContent] = useState<Record<number, DailyContent>>(() => {
    const saved = localStorage.getItem(`bootcamp_content_${pitch.id}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading saved bootcamp content', e);
      }
    }
    return {};
  });

  // State for analyst notes
  const [analystNotes, setAnalystNotes] = useState<Record<number, AnalystNotes>>(() => {
    const saved = localStorage.getItem(`bootcamp_notes_${pitch.id}`);
    return saved ? JSON.parse(saved) : {};
  });

  // State for expanded/collapsed days
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'founder' | 'analyst'>('founder');
  const [generatingDay, setGeneratingDay] = useState<number | null>(null);

  const days = pitch.bootcampProgress;
  const completedCount = days.filter(d => d.completed).length;
  const progress = days.length > 0 ? Math.round((completedCount / days.length) * 100) : 0;

  const isFounder = user?.role === 'founder' && user.id === pitch.founderId;
  const isAnalyst = user?.role === 'analyst' && user.id === pitch.assignedAnalystId;
  const isSeniorAnalyst = user?.role === 'senior_analyst';

  const canVerify = (isAnalyst || isSeniorAnalyst) && progress === 100 && !analystVerified;
  
  // Check if LOI assessment is complete (all criteria assessed)
  const isLOIComplete = loiAssessment.criteria.every(c => c.status !== 'pending');
  
  const canProceedToFunding = progress === 100 && analystVerified;

  // Save daily content to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(dailyContent).length > 0) {
      localStorage.setItem(`bootcamp_content_${pitch.id}`, JSON.stringify(dailyContent));
    }
  }, [dailyContent, pitch.id]);

  // Save analyst notes to localStorage
  useEffect(() => {
    if (Object.keys(analystNotes).length > 0) {
      localStorage.setItem(`bootcamp_notes_${pitch.id}`, JSON.stringify(analystNotes));
    }
  }, [analystNotes, pitch.id]);

  // Save LOI assessment to localStorage
  useEffect(() => {
    localStorage.setItem(`loi_assessment_${pitch.id}`, JSON.stringify(loiAssessment));
  }, [loiAssessment, pitch.id]);

  // Toggle day expansion (works for both founder and analyst)
  const toggleDayExpansion = (day: number) => {
    setExpandedDay(expandedDay === day ? null : day);
  };

  // Close expanded day
  const closeExpandedDay = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedDay(null);
  };

  // Generate AI content for a day (ONLY when analyst clicks the button)
  const handleGenerateContent = async (day: number, week: number, title: string) => {
    setGeneratingDay(day);
    
    try {
      // Determine business type and industry from pitch data
      const businessType = (pitch as any).businessType || 
                          (pitch.companyName?.toLowerCase().includes('tech') ? 'SaaS' : 'Hardware');
      const industry = (pitch as any).industrySector || 'Technology';
      
      // Generate content
      const content = await deepseekApi.generateBootcampContent(
        day, 
        week, 
        title, 
        businessType, 
        industry
      );
      
      // Add metadata
      const contentWithMeta = {
        ...content,
        generatedBy: user?.id,
        generatedAt: new Date().toISOString(),
        aiGenerated: true
      };
      
      // Save to state (which will trigger localStorage save)
      setDailyContent(prev => ({
        ...prev,
        [day]: contentWithMeta
      }));
      
      toast({
        title: "Content generated",
        description: `Day ${day} content has been created and saved.`,
      });
    } catch (error) {
      console.error('Error generating content:', error);
      toast({
        title: "Generation failed",
        description: "There was an error generating content. Please try again.",
        variant: "destructive"
      });
    } finally {
      setGeneratingDay(null);
    }
  };

  // Update LOI criteria status
  const updateLOICriteria = (id: string, field: 'status' | 'notes', value: string) => {
    setLoiAssessment(prev => ({
      ...prev,
      criteria: prev.criteria.map(c => 
        c.id === id ? { ...c, [field]: value } : c
      )
    }));
  };

  // Save LOI assessment
  const saveLOIAssessment = () => {
    setLoiAssessment(prev => ({
      ...prev,
      assessedBy: user?.id,
      assessedAt: new Date().toISOString()
    }));

    toast({
      title: "LOI Assessment Saved",
      description: "Your assessment has been saved successfully.",
    });
  };

  // Handle analyst verification with LOI check
  const handleAnalystVerify = () => {
    if (!canVerify) return;

    if (!isLOIComplete) {
      toast({
        title: "LOI Assessment Required",
        description: "Please complete all LOI assessment criteria before verifying.",
        variant: "destructive"
      });
      return;
    }

    if (loiAssessment.recommendation === 'reject') {
      toast({
        title: "Cannot Verify",
        description: "LOI assessment recommendation is 'Reject'. Please discuss with founder.",
        variant: "destructive"
      });
      return;
    }

    setAnalystVerified(true);
    saveLOIAssessment();

    // Move to diligence stage only after analyst verification
    updatePitchStage(pitch.id, 'diligence');

    toast({
      title: "Bootcamp verified",
      description: "LOI assessment complete. Moving to due diligence.",
    });
  };

  const handleProceedToFunding = () => {
    if (!canProceedToFunding) return;
    
    updatePitchStage(pitch.id, 'funding');
    toast({
      title: "Moving to Funding",
      description: "Bootcamp complete! Proceeding to funding stage.",
    });
  };

  const handleToggleDay = (dayIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isFounder) return;
    toggleBootcampDay(pitch.id, dayIndex);
  };

  return (
    <div className="space-y-6">
      {/* Header with role switcher for analysts */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">28-Day Bootcamp Program</h3>
          <p className="text-sm text-muted-foreground">
            {isAnalyst || isSeniorAnalyst ? 'Analyst View - You can review and customize content' : 'Founder View - Complete daily tasks'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {(isAnalyst || isSeniorAnalyst) && (
            <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)} className="w-[200px]">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="founder">Founder View</TabsTrigger>
                <TabsTrigger value="analyst">Analyst View</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <Badge variant="outline" className={analystVerified ? 'bg-green-100 text-green-700' : ''}>
            {completedCount}/{days.length} days
          </Badge>
        </div>
      </div>

      <Progress value={progress} className="h-2" />

      {/* LOI ASSESSMENT SECTION - Appears when bootcamp is complete but not yet verified */}
      {progress === 100 && !analystVerified && (isAnalyst || isSeniorAnalyst) && activeTab === 'analyst' && (
        <>
          {/* LOI Assessment Card */}
          <Card className="border-purple-200 bg-purple-50/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-purple-600" />
                LOI Readiness Assessment
              </CardTitle>
              <CardDescription>
                Evaluate if bootcamp objectives have been met before issuing Letter of Intent
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* LOI Criteria Grid */}
              <div className="space-y-4">
                {loiAssessment.criteria.map((criterion) => {
                  const Icon = LOI_CRITERIA.find(c => c.id === criterion.id)?.icon || FileText;
                  
                  return (
                    <div key={criterion.id} className="p-4 bg-white rounded-lg border">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-purple-100 rounded">
                            <Icon className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <h4 className="font-medium">{criterion.name}</h4>
                            <p className="text-xs text-muted-foreground">{criterion.description}</p>
                          </div>
                        </div>
                        <Select 
                          value={criterion.status} 
                          onValueChange={(v: any) => updateLOICriteria(criterion.id, 'status', v)}
                        >
                          <SelectTrigger className={`w-[130px] ${
                            criterion.status === 'met' ? 'border-green-300 bg-green-50' :
                            criterion.status === 'partial' ? 'border-yellow-300 bg-yellow-50' :
                            criterion.status === 'not-met' ? 'border-red-300 bg-red-50' :
                            ''
                          }`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="met" className="text-green-600">✅ Met</SelectItem>
                            <SelectItem value="partial" className="text-yellow-600">🟡 Partially Met</SelectItem>
                            <SelectItem value="not-met" className="text-red-600">❌ Not Met</SelectItem>
                            <SelectItem value="pending">⏳ Pending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <Textarea
                        placeholder="Add notes about this criterion..."
                        value={criterion.notes}
                        onChange={(e) => updateLOICriteria(criterion.id, 'notes', e.target.value)}
                        className="text-sm mt-2"
                        rows={2}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Overall Assessment */}
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label className="text-base">Overall Assessment</Label>
                  <Textarea
                    placeholder="Summarize your overall assessment of the founder's readiness for LOI..."
                    value={loiAssessment.overallAssessment}
                    onChange={(e) => setLoiAssessment(prev => ({ ...prev, overallAssessment: e.target.value }))}
                    className="min-h-[100px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-base">LOI Recommendation</Label>
                  <Select 
                    value={loiAssessment.recommendation}
                    onValueChange={(v: any) => setLoiAssessment(prev => ({ ...prev, recommendation: v }))}
                  >
                    <SelectTrigger className={`
                      ${loiAssessment.recommendation === 'approve' ? 'border-green-300 bg-green-50' : ''}
                      ${loiAssessment.recommendation === 'conditional' ? 'border-yellow-300 bg-yellow-50' : ''}
                      ${loiAssessment.recommendation === 'reject' ? 'border-red-300 bg-red-50' : ''}
                    `}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approve" className="text-green-600">✅ Approve - Ready for LOI</SelectItem>
                      <SelectItem value="conditional" className="text-yellow-600">🟡 Conditional Approval</SelectItem>
                      <SelectItem value="reject" className="text-red-600">❌ Reject - Needs More Work</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {loiAssessment.recommendation === 'conditional' && (
                  <div className="space-y-2">
                    <Label>Conditional Terms</Label>
                    <Textarea
                      placeholder="Specify conditions that must be met before LOI can be issued..."
                      value={loiAssessment.conditionalTerms || ''}
                      onChange={(e) => setLoiAssessment(prev => ({ ...prev, conditionalTerms: e.target.value }))}
                      className="min-h-[80px]"
                    />
                  </div>
                )}

                {/* LOI Summary Stats */}
                <div className="grid grid-cols-4 gap-2 pt-4">
                  <div className="p-2 bg-green-50 rounded text-center">
                    <div className="text-lg font-bold text-green-600">
                      {loiAssessment.criteria.filter(c => c.status === 'met').length}
                    </div>
                    <div className="text-xs text-green-700">Met</div>
                  </div>
                  <div className="p-2 bg-yellow-50 rounded text-center">
                    <div className="text-lg font-bold text-yellow-600">
                      {loiAssessment.criteria.filter(c => c.status === 'partial').length}
                    </div>
                    <div className="text-xs text-yellow-700">Partial</div>
                  </div>
                  <div className="p-2 bg-red-50 rounded text-center">
                    <div className="text-lg font-bold text-red-600">
                      {loiAssessment.criteria.filter(c => c.status === 'not-met').length}
                    </div>
                    <div className="text-xs text-red-700">Not Met</div>
                  </div>
                  <div className="p-2 bg-gray-50 rounded text-center">
                    <div className="text-lg font-bold text-gray-600">
                      {loiAssessment.criteria.filter(c => c.status === 'pending').length}
                    </div>
                    <div className="text-xs text-gray-700">Pending</div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={saveLOIAssessment}
                  >
                    Save Draft
                  </Button>
                  <Button 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={saveLOIAssessment}
                  >
                    Submit Assessment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Verification Card - Only for analysts */}
          <Card className={`border-2 ${isLOIComplete ? 'border-yellow-200 bg-yellow-50' : 'border-gray-200 bg-gray-50'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCheck className={`h-5 w-5 ${isLOIComplete ? 'text-yellow-600' : 'text-gray-400'}`} />
                  <div>
                    <p className={`text-sm font-medium ${isLOIComplete ? 'text-yellow-800' : 'text-gray-500'}`}>
                      {isLOIComplete ? 'Ready to Verify Bootcamp' : 'Complete LOI Assessment First'}
                    </p>
                    <p className={`text-xs ${isLOIComplete ? 'text-yellow-600' : 'text-gray-400'}`}>
                      {isLOIComplete 
                        ? 'Founder has completed all bootcamp days. Review and verify to move to due diligence.'
                        : 'Complete all LOI criteria before verifying bootcamp.'}
                    </p>
                  </div>
                </div>
                <Button 
                  size="sm"
                  onClick={handleAnalystVerify}
                  disabled={!isLOIComplete}
                  className={isLOIComplete ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-400'}
                >
                  Verify & Move to Due Diligence
                </Button>
              </div>

              {!isLOIComplete && (
                <div className="mt-3 p-2 bg-red-50 rounded flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                  <p className="text-xs text-red-600">
                    Complete all {loiAssessment.criteria.length} LOI criteria before verifying.
                    {loiAssessment.criteria.filter(c => c.status === 'pending').length} criteria still pending.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Founder waiting message - Show when bootcamp complete but not verified */}
      {progress === 100 && !analystVerified && isFounder && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-800">Bootcamp Complete - Awaiting Verification</p>
                <p className="text-xs text-blue-600">
                  You've completed all bootcamp days! The analyst will review your work 
                  and verify completion. You'll be notified when you can proceed to due diligence.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success message - both complete (now moves to diligence) */}
      {progress === 100 && analystVerified && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Bootcamp Verified!</p>
                  <p className="text-xs text-green-600">LOI issued. Proceeding to due diligence.</p>
                </div>
              </div>
              {(isAnalyst || isSeniorAnalyst) && (
                <Button 
                  size="sm"
                  onClick={() => updatePitchStage(pitch.id, 'diligence')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Go to Due Diligence
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bootcamp Days with AI Content */}
      <div className="space-y-8">
        {WEEK_TITLES.map((weekTitle, weekIdx) => {
          const weekDays = days.filter(d => d.week === weekIdx + 1);
          const weekComplete = weekDays.filter(d => d.completed).length;
          
          return (
            <div key={weekIdx} className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-semibold">{weekTitle}</h4>
                <span className="text-sm text-muted-foreground">{weekComplete}/7 completed</span>
              </div>

              <div className="space-y-4">
                {weekDays.map((day) => {
                  const dayIndex = days.findIndex(d => d.day === day.day);
                  const content = dailyContent[day.day];
                  const notes = analystNotes[day.day];
                  const isExpanded = expandedDay === day.day;

                  return (
                    <Card key={day.day} className={`overflow-hidden ${
                      day.completed ? 'border-green-200' : ''
                    }`}>
                      {/* Day Header - Click to expand */}
                      <div 
                        className={`p-4 cursor-pointer hover:bg-accent/50 transition-colors ${
                          day.completed ? 'bg-green-50' : ''
                        } ${isExpanded ? 'border-b' : ''}`}
                        onClick={() => toggleDayExpansion(day.day)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={day.completed}
                              onCheckedChange={(checked) => {
                                if (isFounder) {
                                  toggleBootcampDay(pitch.id, dayIndex);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              disabled={!isFounder || analystVerified}
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">Day {day.day}:</span>
                                <span>{day.title}</span>
                                {content?.aiGenerated && (
                                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                    <Sparkles className="h-3 w-3 mr-1" />
                                    AI Generated
                                  </Badge>
                                )}
                                {content && !content.aiGenerated && (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                    <PenTool className="h-3 w-3 mr-1" />
                                    Customized
                                  </Badge>
                                )}
                              </div>
                              {notes?.founderUnderstanding && isAnalyst && activeTab === 'analyst' && (
                                <div className="flex items-center gap-1 mt-1">
                                  <ThumbsUp className="h-3 w-3 text-green-600" />
                                  <span className="text-xs">Understanding: {notes.founderUnderstanding}/5</span>
                                </div>
                              )}
                              {/* Show content status for founder */}
                              {isFounder && content && (
                                <div className="flex items-center gap-1 mt-1">
                                  <Eye className="h-3 w-3 text-blue-600" />
                                  <span className="text-xs text-blue-600">Content available</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!content && (isAnalyst || isSeniorAnalyst) && activeTab === 'analyst' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGenerateContent(day.day, weekIdx + 1, day.title);
                                }}
                                disabled={generatingDay === day.day}
                              >
                                {generatingDay === day.day ? (
                                  <>Generating...</>
                                ) : (
                                  <>
                                    <Brain className="h-4 w-4 mr-2" />
                                    Generate
                                  </>
                                )}
                              </Button>
                            )}
                            <Button variant="ghost" size="sm">
                              {isExpanded ? 'Close' : 'View Details'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content - Shows for both founder and analyst when expanded */}
                      {isExpanded && (
                        <div className="p-4 border-t bg-muted/20">
                          {content ? (
                            <div className="space-y-4">
                              {/* Learning Objectives */}
                              <div className="space-y-2">
                                <h5 className="font-medium flex items-center gap-2">
                                  <Target className="h-4 w-4" />
                                  Learning Objectives
                                </h5>
                                <ul className="list-disc list-inside text-sm space-y-1">
                                  {content.learningObjectives?.map((obj, i) => (
                                    <li key={i}>{obj}</li>
                                  ))}
                                </ul>
                              </div>

                              {/* Key Concepts */}
                              <div className="space-y-2">
                                <h5 className="font-medium flex items-center gap-2">
                                  <Lightbulb className="h-4 w-4" />
                                  Key Concepts
                                </h5>
                                <div className="space-y-3">
                                  {content.keyConcepts?.map((concept, i) => (
                                    <div key={i} className="p-3 bg-muted/30 rounded">
                                      <p className="font-medium text-sm">{concept.term}</p>
                                      <p className="text-sm text-muted-foreground mt-1">{concept.definition}</p>
                                      {concept.examples && concept.examples.length > 0 && (
                                        <div className="mt-2">
                                          <p className="text-xs font-medium">Examples:</p>
                                          <ul className="list-disc list-inside text-xs text-muted-foreground">
                                            {concept.examples.map((ex, j) => (
                                              <li key={j}>{ex}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Tasks */}
                              <div className="space-y-4">
                                <h5 className="font-medium flex items-center gap-2">
                                  <BookOpen className="h-4 w-4" />
                                  Today's Tasks
                                </h5>
                                {content.tasks?.map((task, i) => (
                                  <Card key={i}>
                                    <CardContent className="pt-4">
                                      <h6 className="font-medium">{task.title}</h6>
                                      <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                                      <div className="mt-2">
                                        <span className="text-xs font-medium">Deliverables:</span>
                                        <ul className="list-disc list-inside text-xs text-muted-foreground">
                                          {task.deliverables?.map((d, j) => (
                                            <li key={j}>{d}</li>
                                          ))}
                                        </ul>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-2">
                                        Estimated time: {task.estimatedTime} hours
                                      </p>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>

                              {/* Quiz */}
                              {content.quiz && content.quiz.length > 0 && (
                                <div className="space-y-2">
                                  <h5 className="font-medium">Quick Quiz</h5>
                                  {content.quiz.map((q, i) => (
                                    <Card key={i}>
                                      <CardContent className="pt-4">
                                        <p className="text-sm font-medium">{q.question}</p>
                                        <div className="mt-2 space-y-1">
                                          {q.options.map((opt, j) => (
                                            <div key={j} className="flex items-center gap-2 text-sm">
                                              <input type="radio" name={`quiz-${day.day}-${i}`} id={`q${i}o${j}`} />
                                              <label htmlFor={`q${i}o${j}`}>{opt}</label>
                                            </div>
                                          ))}
                                        </div>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              )}

                              {/* Analyst Notes - Only show if they exist */}
                              {notes && (
                                <Card className="bg-blue-50">
                                  <CardContent className="pt-4">
                                    <h5 className="font-medium flex items-center gap-2 text-blue-700">
                                      <MessageSquare className="h-4 w-4" />
                                      Analyst Notes
                                    </h5>
                                    <p className="text-sm mt-1">{notes.notes}</p>
                                    {notes.resources && notes.resources.length > 0 && (
                                      <div className="mt-2">
                                        <span className="text-xs font-medium">Resources:</span>
                                        <ul className="list-disc list-inside text-xs text-blue-600">
                                          {notes.resources.map((url, i) => (
                                            <li key={i}><a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">{url}</a></li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              )}

                              {/* Close Button */}
                              <div className="flex justify-end">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={closeExpandedDay}
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Close
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-8 text-muted-foreground">
                              <Brain className="h-12 w-12 mx-auto mb-3 opacity-20" />
                              <p>No content generated yet</p>
                              <p className="text-sm">Analyst will generate AI content soon</p>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="mt-4"
                                onClick={closeExpandedDay}
                              >
                                <X className="h-4 w-4 mr-2" />
                                Close
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}